import { useCombobox } from 'downshift';
import React, { useState, useMemo, useCallback } from 'react';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { noop } from '../../utils/noop.esm.js';
import '@popperjs/core';
import { Button } from '../Button/Button.esm.js';
import '@panwds/icons';
import '../Icon/ImgIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { Portal } from '../Portal/Portal.esm.js';
import { SearchInput } from './SearchInput.esm.js';
import { SelectList } from './SelectList.esm.js';
import { useDraftSelections } from './useDraftSelections.esm.js';
import { useSelectAll } from './useSelectAll.esm.js';
import { sortBySelected, onSelectedItemChange } from './utils.esm.js';

const emptyItems = [];
const defaultFilterFn = (filter, value) => value.toLowerCase().includes(filter.toLowerCase());
const defaultItemToString = (item) => {
    return item ? String(item.children) : "";
};
/**
 * This is the dropdown for the select with Search.
 * It does not control opening and should only be rendered when it is supposed to be open.
 */
function Dropdown({ containerRef, anchorRef, dataMetrics, dataTestId, items = emptyItems, filterFn = defaultFilterFn, isOpen, itemToString = defaultItemToString, enableMultiSelect = false, enableSelectAll = false, isLoading = false, labelId, darkMode, menuStyle, onChange = noop, onMenuKeyEnter, onOpenChange, onSearch, selectedItem, toggleButtonId, formatOption, fitToItemWidth, fullWidth, listItemSize, itemsShown, addClassName, allowDraftSelections = false, selectAllLabelHandler, }) {
    const [filter, setFilter] = useState("");
    // needed for apply / cancel variant
    const [cachedItems, setSelectedItems] = useState(selectedItem ? [selectedItem].flat() : []);
    // needed for single / multi variants
    let selectedItems = selectedItem ? [selectedItem].flat() : [];
    /**
     * use the state if following condition is met because the custom
     * hook would be updating it using setSelectedItems
     */
    if (allowDraftSelections && enableMultiSelect) {
        selectedItems = cachedItems;
    }
    const selectedValues = useMemo(() => selectedItems.map((item) => item.value), [selectedItems, cachedItems]);
    const filteredItems = useMemo(() => {
        let filtered = [...items];
        if (filter && !onSearch) {
            filtered = filtered.filter((item) => filterFn(filter, itemToString(item)));
        }
        if (enableMultiSelect)
            filtered = filtered.sort(sortBySelected(selectedValues));
        return filtered;
    }, [items, filter]);
    const { showSelectAll, selectAllItem, allSelected } = useSelectAll({
        enableSelectAll,
        items: filteredItems,
        selectedValues,
        enableMultiSelect,
        selectAllLabelHandler,
    });
    const filterItems = useCallback((value) => {
        setFilter(value !== null && value !== void 0 ? value : "");
        onSearch === null || onSearch === void 0 ? void 0 : onSearch(value !== null && value !== void 0 ? value : "");
    }, [onSearch, items]);
    const { draftSelectionsAllowed, onDraftChange, onRealChange, applyChanges, discardChanges, selectionsChanged, } = useDraftSelections({
        enableMultiSelect,
        allowDraftSelections,
        setSelectedItems,
        selectedItem,
        onChange,
    });
    const normalizedItems = enableSelectAll && showSelectAll
        ? [selectAllItem, ...filteredItems]
        : filteredItems;
    const { getComboboxProps, getInputProps, getItemProps, getMenuProps, highlightedIndex, } = useCombobox({
        isOpen: true,
        items: normalizedItems,
        inputValue: filter,
        itemToString,
        labelId,
        toggleButtonId,
        onStateChange: (action) => {
            var _a, _b;
            const { type } = action;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (!enableMultiSelect) {
                        (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        onOpenChange(false);
                    }
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownEscape:
                case useCombobox.stateChangeTypes.InputBlur:
                    (_b = anchorRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    onOpenChange(false);
                    filterItems("");
                    break;
            }
        },
        stateReducer(state, actionAndChanges) {
            const { changes, type } = actionAndChanges;
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        highlightedIndex: state.highlightedIndex,
                    };
                default:
                    return changes;
            }
        },
        selectedItem: enableMultiSelect ? null : selectedItems[0] || null,
        onSelectedItemChange: (changes) => {
            onSelectedItemChange({
                changes,
                enableMultiSelect,
                selectedItems,
                onChange: draftSelectionsAllowed ? onDraftChange : onRealChange,
                selectedValues,
                selectAllItem,
                allSelected,
                itemsForSelectAll: filteredItems,
            });
        },
    });
    const draftSelectionsControl = draftSelectionsAllowed ? (React.createElement("div", { className: "tw-flex tw-justify-end tw-sticky tw-top-0 tw-z-10 tw-p-2 tw-bg-white dark:tw-bg-gray-1000" },
        React.createElement(Button, { appearance: "secondary", onClick: () => {
                discardChanges && discardChanges();
                onOpenChange(false);
            }, addClassName: "tw-mr-2", disabled: !selectionsChanged }, "Cancel"),
        React.createElement(Button, { appearance: "primary", onClick: () => {
                applyChanges && applyChanges();
                onOpenChange(false);
            }, disabled: !selectionsChanged }, "Apply"))) : undefined;
    return (React.createElement(Portal, null,
        React.createElement("div", { ...getComboboxProps({ ref: containerRef }), className: classNames(addClassName, "tw-z-30", darkMode && "dark"), "data-testid": "portal-div" },
            React.createElement(SelectList, { dataMetrics: dataMetrics, dataTestId: dataTestId, isOpen: isOpen, menuStyle: menuStyle, getMenuProps: getMenuProps, onMenuKeyEnter: onMenuKeyEnter, items: normalizedItems, highlightedIndex: highlightedIndex, selectedItems: selectedItems, selectAllItem: selectAllItem, enableMultiSelect: enableMultiSelect, getItemProps: getItemProps, showSelectAll: showSelectAll, search: React.createElement(SearchInput, { isLoading: isLoading, filter: filter, filterItems: filterItems, getInputProps: getInputProps }), selectedValues: selectedValues, formatOption: formatOption, fitToItemWidth: fullWidth ? false : fitToItemWidth, itemsShown: itemsShown, listItemSize: listItemSize, draftSelectionsControl: draftSelectionsControl }))));
}

export { Dropdown };
//# sourceMappingURL=Dropdown.esm.js.map
